import { useState } from "react";
import { isOfflineMutation } from "./model";
import type { StaffPaneProps } from "./console-types";
import "./owner-email.css";

/**
 * B1 (audit F115, 2026-08-11): "Překlep v e-mailu klienta nejde opravit
 * odnikud. Ani parťák, ani admin, ani API."
 *
 * A typo at creation meant the client never got the login link and never got a
 * single later notification, and the only way out was a hand-written row in
 * the database. This is the way out from the console.
 *
 * 🔴 The address is typed TWICE (the F105 ownership form's idiom). It is the
 * one field nobody can verify by looking: the whole reason we are here is that
 * somebody read a wrong address as right. The second field is compared
 * character for character before the button unlocks.
 *
 * 🔴 A refusal is mapped through an ALLOWLIST, never by probing whether `t()`
 * handed its own key back: `t` THROWS on a missing key, and a throw inside the
 * click handler leaves the button disabled forever (F106).
 */
const EMAIL_ERROR_CODES = [
  "email_taken", "email_unchanged", "invalid_request", "not_found", "state_conflict",
  "idempotency_mismatch", "forbidden", "csrf_invalid", "origin_invalid", "impersonation_readonly"
] as const;

function looksLikeEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) && value.length <= 254;
}

export function OwnerEmailCard(props: StaffPaneProps) {
  const current = props.detail.client.owner?.email ?? props.detail.client.email ?? "";
  const [email, setEmail] = useState("");
  const [repeat, setRepeat] = useState("");
  const [armed, setArmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const normalized = email.trim().toLowerCase();
  const repeated = repeat.trim().toLowerCase();
  const valid = looksLikeEmail(normalized);
  const matches = normalized.length > 0 && normalized === repeated;
  const unchanged = normalized.length > 0 && normalized === current.trim().toLowerCase();
  // The reason a control is closed is printed BESIDE it, never hidden in a
  // title attribute a disabled element can never show (S2-19).
  const blockedKey = !normalized ? null
    : !valid ? "staffConsole.ownerEmailChange.invalid"
      : unchanged ? "staffConsole.ownerEmailChange.same"
        : !matches ? "staffConsole.ownerEmailChange.mismatch"
          : null;

  async function save() {
    setBusy(true);
    setError(null);
    const response = await props.run(
      `/api/admin/clients/${props.detail.client.id}/owner-email`,
      { method: "PATCH", body: { email: normalized } }
    );
    if (response.ok) {
      setSaved(normalized);
      setEmail("");
      setRepeat("");
      setArmed(false);
      await props.refresh();
    } else if (isOfflineMutation(response)) {
      setError(props.t("staffConsole.mutationOffline"));
    } else {
      let code = "";
      try {
        const body = await response.json() as { error?: string };
        if (typeof body.error === "string") code = body.error;
      } catch { /* the generic sentence below covers an unreadable body */ }
      const named = (EMAIL_ERROR_CODES as readonly string[]).includes(code);
      setError(props.t(named
        ? `staffConsole.ownerEmailChange.error.${code}`
        : "staffConsole.ownerEmailChange.error.generic"));
    }
    setBusy(false);
  }

  return (
    <article className="staff-card owner-email-card" data-admin-tool="owner-email">
      <h3>{props.t("staffConsole.ownerEmailChange.title")}</h3>
      <p className="staff-card-hint">{props.t("staffConsole.ownerEmailChange.hint")}</p>
      <dl className="staff-definition">
        <div>
          <dt>{props.t("staffConsole.ownerEmailChange.current")}</dt>
          <dd>{current || props.t("staffConsole.none")}</dd>
        </div>
      </dl>
      <div className="owner-email-fields">
        <label htmlFor="owner-email-new">
          <span>{props.t("staffConsole.ownerEmailChange.newLabel")}</span>
        </label>
        <input
          id="owner-email-new"
          type="email"
          autoComplete="off"
          value={email}
          placeholder={props.t("staffConsole.ownerEmailExample")}
          onChange={(event) => { setEmail(event.target.value); setArmed(false); setError(null); }}
        />
        <label htmlFor="owner-email-repeat">
          <span>{props.t("staffConsole.ownerEmailChange.repeatLabel")}</span>
        </label>
        <input
          id="owner-email-repeat"
          type="email"
          autoComplete="off"
          value={repeat}
          placeholder={props.t("staffConsole.ownerEmailExample")}
          onChange={(event) => { setRepeat(event.target.value); setArmed(false); setError(null); }}
        />
      </div>
      {blockedKey && <p className="staff-card-hint owner-email-blocked">{props.t(blockedKey)}</p>}
      {armed ? (
        <div className="owner-email-confirm" role="alert">
          <p>
            {props.t("staffConsole.ownerEmailChange.confirm")
              .replace("{client}", props.detail.client.name)
              .replace("{email}", normalized)}
          </p>
          <p className="staff-card-hint">{props.t("staffConsole.ownerEmailChange.consequences")}</p>
          {error && <p className="message error-message" role="alert">{error}</p>}
          <div className="button-row">
            <button
              className="primary-button compact-button"
              type="button"
              disabled={busy}
              onClick={() => void save()}
            >
              {props.t(busy ? "staffConsole.ownerEmailChange.saving" : "staffConsole.ownerEmailChange.confirmSave")}
            </button>
            <button
              className="quiet-link"
              type="button"
              disabled={busy}
              onClick={() => { setArmed(false); setError(null); }}
            >
              {props.t("common.cancel")}
            </button>
          </div>
        </div>
      ) : (
        <div className="button-row">
          <button
            className="secondary-button compact-button"
            type="button"
            disabled={Boolean(blockedKey) || !matches || !valid}
            onClick={() => { setArmed(true); setSaved(null); setError(null); }}
          >
            {props.t("staffConsole.ownerEmailChange.button")}
          </button>
          {saved && (
            <span className="message success-message">
              {props.t("staffConsole.ownerEmailChange.saved").replace("{email}", saved)}
            </span>
          )}
          {!armed && error && <span className="message error-message" role="alert">{error}</span>}
        </div>
      )}
    </article>
  );
}
